// Adapts a WP9 invoice (with its frozen business_details / bank_details snapshots
// and invoice_lines) into the shape the existing invoicePdf.js builder expects, then
// downloads the PDF. The builder already renders a per-rate VAT breakdown + DRS, so we
// only reshape — no duplicate PDF logic.
//
// ⛔ SEND-0 (26 Oct 2026): a missing snapshot field is passed through AS "" and
// falls back to NOTHING. This file used to delete its own empty keys on purpose,
// so that invoicePdf's DEFAULT_BUSINESS would fill them — which is how Msquared's
// address, VAT number and bank account reached tenants' invoices. That fallback
// object is gone; so is the delete loop that fed it.

import { downloadInvoicePDF } from "../lib/invoicePdf";

// SEND-1 exports this so emailInvoice can build its business block from the SAME
// frozen snapshot the PDF uses. One normaliser, so a mail and its attachment can
// never disagree about who sent the invoice.
export function businessFromSnapshot(b = {}, bank = {}) {
  const addr = [b.address_line1, b.address_line2, [b.city, b.county].filter(Boolean).join(", "), b.postal_code, b.country]
    .filter(Boolean).join("\n");
  // Keys are always present and always strings. The builder decides what to print
  // and what to omit; this adapter's job is only to reshape, and an empty string
  // is the honest answer for a field the tenant has not filled in.
  return {
    name: b.trading_name || b.legal_name || "",
    tagline: b.website || "",
    address: addr || "",
    email: b.email || "",
    phone: b.phone || "",
    vat: b.vat_number || "",
    bank_account_name: bank.bank_account_name || "",
    bank_name: bank.bank_name || "",
    iban: bank.iban || "",
    bic: bank.bic || "",
    logo_url: b.logo_url || "",   // CC11: carry the tenant logo into the branded header
  };
}

/** Map + trigger a browser download of the invoice PDF from its own snapshots. */
export function downloadInvoice(invoice) {
  const lines = (invoice.invoice_lines || invoice.lines || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const items = lines.map((l) => ({
    description: l.description || "",
    unit: "",
    quantity: l.quantity,
    unit_price: l.unit_price,
    vat_rate: l.vat_rate,
    drs_amount: l.drs ? (l.drs_amount || 0) : 0,
    total: l.line_total != null ? l.line_total : (Number(l.quantity) || 0) * (Number(l.unit_price) || 0),
  }));
  const mapped = {
    invoice_number: invoice.invoice_number,
    issue_date: invoice.issue_date,
    due_date: invoice.due_date,
    payment_terms: invoice.payment_terms,
    status: invoice.status,
    customer_name: invoice.customer_name,
    customer_email: invoice.customer_email,
    customer_address: invoice.customer_address,
    customer_vat: invoice.customer_vat,
    notes: invoice.notes,
    subtotal: invoice.subtotal,
    total_vat: invoice.total_vat,
    total_drs: invoice.total_drs,
    total_amount: invoice.total_amount,
    items,
  };
  downloadInvoicePDF(mapped, businessFromSnapshot(invoice.business_details, invoice.bank_details));
}
